/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from "react"
import type { PageInfo } from "./types"
import { PDF_VIEWER_BUFFER_PAGES } from "./config"



interface VisibleRange {
	start: number
	end: number
}

interface UsePageRendererResult {
	renderedPages: Set<number>
	canvasRefs: React.MutableRefObject<Map<number, HTMLCanvasElement>>
}

export const usePageRenderer = (
	pdfDoc: React.MutableRefObject<any>,
	pageInfos: PageInfo[],
	visibleRange: VisibleRange,
	baseScale: number
): UsePageRendererResult => {
	const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set())
	const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map())
	const renderingRef = useRef<Set<number>>(new Set())

	// Reset rendered pages when scale changes (layout recalculated)
	useEffect(() => {
		setRenderedPages(new Set())
		renderingRef.current.clear()
	}, [baseScale])

	const renderPage = useCallback(
		async (pageNumber: number) => {
			if (!pdfDoc.current || renderingRef.current.has(pageNumber)) return

			renderingRef.current.add(pageNumber)

			try {
				const page = await pdfDoc.current.getPage(pageNumber)
				const scaledViewport = page.getViewport({ scale: baseScale })
				const devicePixelRatio =
					typeof window !== "undefined" && window.devicePixelRatio
						? window.devicePixelRatio
						: 1
				const dpr = Math.min(3, Math.max(1, devicePixelRatio))

				let canvas = canvasRefs.current.get(pageNumber)
				if (!canvas) {
					canvas = document.createElement("canvas")
					canvas.className = "pdf-page-canvas"
					canvas.style.display = "block"
					canvasRefs.current.set(pageNumber, canvas)
				}

				const context = canvas.getContext("2d")
				if (!context) return

				canvas.style.width = `${scaledViewport.width}px`
				canvas.style.height = `${scaledViewport.height}px`

				canvas.width = Math.max(1, Math.floor(scaledViewport.width * dpr))
				canvas.height = Math.max(1, Math.floor(scaledViewport.height * dpr))

				const renderContext = {
					canvasContext: context,
					viewport: scaledViewport,
					transform:
						dpr !== 1 ? ([dpr, 0, 0, dpr, 0, 0] as any) : undefined,
				}

				await page.render(renderContext).promise
				setRenderedPages(prev => new Set([...prev, pageNumber]))
			} catch (error) {
				console.error(`Error rendering page ${pageNumber}:`, error)
			} finally {
				renderingRef.current.delete(pageNumber)
			}
		},
		[pdfDoc, baseScale]
	)

	// Render visible pages
	useEffect(() => {
		if (pageInfos.length === 0) return

		const start =
			visibleRange.start === 0 && visibleRange.end === 0
				? 0
				: visibleRange.start
		const end =
			visibleRange.start === 0 && visibleRange.end === 0
				? Math.min(PDF_VIEWER_BUFFER_PAGES * 2, pageInfos.length - 1)
				: visibleRange.end

		for (let i = start; i <= end; i++) {
			const pageNumber = pageInfos[i].pageNumber
			if (!renderedPages.has(pageNumber)) {
				renderPage(pageNumber)
			}
		}
	}, [visibleRange, pageInfos, renderedPages, renderPage])

	return { renderedPages, canvasRefs }
}
